import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  Receipt,
  Settings as SettingsIcon,
  ShoppingBag,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";


import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/billing/history")({
  head: () => ({
    meta: [
      { title: "支払い履歴 - KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: BillingHistoryPage,
});

type PaymentRow = {
  id: string;
  created_at: string;
  amount: number;
  amount_tax: number | null;
  amount_excl_tax: number | null;
  currency: string;
  status: string | null;
  type: string | null;
  description: string | null;
  stripe_payment_id: string | null;
  stripe_invoice_id: string | null;
};

const PAGE_SIZE = 20;
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 2023 }, (_, i) => CURRENT_YEAR - i);

type FilterTab = "all" | "subscription" | "pack";

function statusBadge(status: string | null) {
  if (status === "succeeded") {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/15 border-emerald-500/30">
        成功
      </Badge>
    );
  }
  if (status === "failed") {
    return (
      <Badge className="bg-red-500/15 text-red-600 hover:bg-red-500/15 border-red-500/30">
        失敗
      </Badge>
    );
  }
  if (status === "refunded") {
    return (
      <Badge variant="secondary" className="bg-muted text-muted-foreground">
        返金
      </Badge>
    );
  }
  return <Badge variant="outline">{status ?? "-"}</Badge>;
}

function describePayment(row: PaymentRow): string {
  if (row.description) return row.description;
  // Fallback for legacy rows without description
  if (row.type === "subscription" || row.type === "subscription_renewal") {
    return "サブスクリプション";
  }
  if (row.type === "pack") return "クレジット購入";
  return row.type ?? "—";
}

function formatJpy(n: number | null | undefined): string {
  if (n == null) return "—";
  return `¥${n.toLocaleString("ja-JP")}`;
}

function csvEscape(v: string | number | null | undefined): string {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function BillingHistoryPage() {
  const [year, setYear] = useState<string>("all");
  const [month, setMonth] = useState<string>("all");
  const [tab, setTab] = useState<FilterTab>("all");
  const [page, setPage] = useState(0);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const queryKey = useMemo(
    () => ["payment_history", { year, month, tab }] as const,
    [year, month, tab],
  );

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: async (): Promise<PaymentRow[]> => {
      let q = supabase
        .from("payment_history")
        .select(
          "id, created_at, amount, amount_tax, amount_excl_tax, currency, status, type, description, stripe_payment_id, stripe_invoice_id",
        )
        .order("created_at", { ascending: false })
        .limit(1000);

      if (year !== "all") {
        const y = parseInt(year, 10);
        const from = month === "all"
          ? new Date(y, 0, 1)
          : new Date(y, parseInt(month, 10) - 1, 1);
        const to = month === "all"
          ? new Date(y + 1, 0, 1)
          : new Date(y, parseInt(month, 10), 1);
        q = q.gte("created_at", from.toISOString()).lt("created_at", to.toISOString());
      }

      if (tab === "subscription") {
        q = q.in("type", ["subscription", "subscription_renewal"]);
      } else if (tab === "pack") {
        q = q.eq("type", "pack");
      }


      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as PaymentRow[];
    },
    staleTime: 30_000,
  });

  const rows = data ?? [];
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  function resetPage<T>(setter: (v: T) => void, value: T) {
    setter(value);
    setPage(0);
  }

  async function handleReceipt(row: PaymentRow) {
    setDownloadingId(row.id);
    try {
      const { data, error } = await supabase.functions.invoke("get-receipt-url", {
        body: { payment_id: row.id },
      });
      if (error) throw error;
      const url = (data as { url?: string } | null)?.url;
      if (!url) throw new Error("RECEIPT_UNAVAILABLE");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      const msg = (e as Error).message;
      if (msg.includes("RECEIPT_UNAVAILABLE")) {
        toast.error("この支払いには領収書が紐づいていません");
      } else {
        toast.error("領収書の取得に失敗しました");
      }
    } finally {
      setDownloadingId(null);
    }
  }

  function handleCsvExport() {
    if (rows.length === 0) {
      toast.info("出力できるデータがありません");
      return;
    }
    const header = [
      "日付",
      "内容",
      "金額（税込）",
      "金額（税抜）",
      "消費税額",
      "ステータス",
      "取引ID",
      "領収書ID",
    ];
    const lines = [header.map(csvEscape).join(",")];
    for (const r of rows) {
      lines.push(
        [
          format(new Date(r.created_at), "yyyy/MM/dd HH:mm"),
          describePayment(r),
          r.amount ?? 0,
          r.amount_excl_tax ?? "",
          r.amount_tax ?? "",
          r.status ?? "",
          r.stripe_payment_id ?? "",
          r.stripe_invoice_id ?? "",
        ]
          .map(csvEscape)
          .join(","),
      );
    }
    const csv = "\uFEFF" + lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payment-history-${format(new Date(), "yyyyMMdd-HHmm")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="container mx-auto max-w-6xl p-4 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Receipt className="h-6 w-6" />
            支払い履歴
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            過去のサブスクリプション・クレジット購入を確認できます
          </p>
        </div>
        <div className="flex flex-wrap gap-2 self-start md:self-auto">
          <Button asChild variant="outline">
            <Link to="/settings">
              <SettingsIcon className="h-4 w-4 mr-2" />
              サブスクリプション管理・解約
            </Link>
          </Button>
          <Button onClick={handleCsvExport} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            CSVでダウンロード
          </Button>
        </div>

      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">フィルター</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs value={tab} onValueChange={(v) => resetPage(setTab, v as FilterTab)}>
            <TabsList className="grid grid-cols-3 w-full md:w-auto">
              <TabsTrigger value="all">すべて</TabsTrigger>
              <TabsTrigger value="subscription">サブスク</TabsTrigger>
              <TabsTrigger value="pack">クレジット購入</TabsTrigger>
            </TabsList>
          </Tabs>


          <div className="flex flex-wrap gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">年度</label>
              <Select value={year} onValueChange={(v) => resetPage(setYear, v)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべて</SelectItem>
                  {YEARS.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}年
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">月</label>
              <Select
                value={month}
                onValueChange={(v) => resetPage(setMonth, v)}
                disabled={year === "all"}
              >
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべて</SelectItem>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <SelectItem key={m} value={String(m)}>
                      {m}月
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Card>
          <CardContent className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : isError ? (
        <Card>
          <CardContent className="p-8 text-center space-y-3">
            <p className="text-muted-foreground">読み込みに失敗しました</p>
            <Button onClick={() => refetch()} variant="outline">
              再試行
            </Button>
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="p-10 md:p-16 text-center space-y-6">
            <FileText className="h-14 w-14 mx-auto text-muted-foreground/50" />
            <h2 className="text-xl md:text-2xl font-semibold">まだ支払い履歴がありません</h2>
            <p className="text-sm text-muted-foreground">
              プランを始めるか、クレジットを購入すると履歴がここに表示されます
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild>
                <Link to="/credits-shop">
                  <Sparkles className="h-4 w-4 mr-2" />
                  プランをアップグレード
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/credits-shop">
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  クレジットを購入
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">日付</TableHead>
                    <TableHead>内容</TableHead>
                    <TableHead className="text-right">金額（税込）</TableHead>
                    <TableHead>ステータス</TableHead>
                    <TableHead className="text-right">領収書</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="whitespace-nowrap text-sm">
                        {format(new Date(r.created_at), "yyyy/MM/dd HH:mm")}
                      </TableCell>
                      <TableCell className="text-sm">{describePayment(r)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatJpy(r.amount)}
                      </TableCell>
                      <TableCell>{statusBadge(r.status)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={
                            r.status !== "succeeded" ||
                            downloadingId === r.id ||
                            (!r.stripe_invoice_id && !r.stripe_payment_id)
                          }
                          onClick={() => handleReceipt(r)}
                        >
                          {downloadingId === r.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between text-sm">
            <div className="text-muted-foreground">
              {rows.length}件中 {page * PAGE_SIZE + 1}–
              {Math.min((page + 1) * PAGE_SIZE, rows.length)}件を表示
              {isFetching && <Loader2 className="inline h-3 w-3 ml-2 animate-spin" />}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="tabular-nums">
                {page + 1} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
